<template>
  <div
    class="sensor-action"
    :class="{'sensor-action--action': actions.length}"
  >
    <div class="sensor-action__grid">
      <div>
        <h4 class="g-title g-title--subtitle">
          {{ $t('messages.sensors.sdActions.action') }}
        </h4>
        <button
          class="g-btn g-btn--semi-rounded"
          :class="{'disabled': actions.length === 0}"
          @click.stop="toggle()"
        >
          <font-awesome-icon :icon="computedAngle" />
        </button>
      </div>
      <input-element
        v-if="actionData.status && actionData.status.seqno"
        class="input-element--center min-50"
        :title="$t('messages.sensors.sdActions.seqno')"
        :disabled="true"
        :text="computedAction.seqno"
      />
      <select-element
        v-if="actionData.status && actionData.status.action"
        class="select-element--full"
        :title="$t('messages.sensors.sdActions.action')"
        :value="computedAction.action"
        :list="actionDropdown"
        @change="setValue($event.value, 'action')"
      />
      <select-element
        v-if="actionData.status && actionData.status.itemtype"
        class="select-element--full"
        :title="$t('messages.sensors.sdActions.itemtype')"
        :value="computedAction.itemtype"
        :list="types"
        @change="setValue($event.value, 'itemtype')"
      />
      <input-element
        v-if="actionData.status && actionData.status.itemno"
        class="input-element--center min-50"
        :title="$t('messages.sensors.sdActions.itemno')"
        :type="'number'"
        :min="1"
        :text="computedAction.itemno !== 0 ? computedAction.itemno : null"
        :disabled="computedAction.itemno === 0"
        :max="itemNoRange[computedAction.itemtype] ? itemNoRange[computedAction.itemtype] : ''"
        @change="setValue($event, 'itemno')"
      />
      <div
        v-if="actionData.status && actionData.status.itemno && actionData.status.all"
        :class="{'active': computedAction.itemno === 0}"
      >
        <h4 class="g-title g-title--subtitle g-text--center">
          {{ $t('messages.sensors.sdActions.all') }}
        </h4>
        <div
          class="g-btn g-btn--rounded g-btn--toggle"
          type="button"
          name="button"
          @click="setValue(computedAction.itemno === 0 ? 1 : 0, 'itemno')"
        />
      </div>
      <input-element
        v-if="actionData.status && actionData.status.delay"
        class="input-element--center min-50"
        :title="$t('messages.sensors.sdActions.delay')"
        :type="'number'"
        :min="0"
        :step="actionData.delayfactor"
        :text="computedAction.delay"
        @change="setValue($event, 'delay')"
      />
      <input-element
        v-if="actionData.status && actionData.status.message"
        class="select-element--full"
        :title="$t('messages.sensors.sdActions.message')"
        :text="computedAction.message"
        @change="setValue($event, 'message')"
      />
      <div>
        <h4 class="g-title g-title--subtitle">
          {{ $t('messages.sensors.sdActions.delete') }}
        </h4>
        <button
          class="g-btn g-btn--semi-rounded g-btn--red"
          @click.stop="deleteClicked(computedAction)"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>
    
    <transition-group
      v-show="showActions"
      name="alarm--transition"
      tag="div"
      class="sensor-alarm__actions"
    >
      <EditSensorDecoderAction
        v-for="action in actions"
        :key="action.data.id"
        :alarmid="alarmid"
        :action-data="action"
        :action-dropdown="actionDropdown"
        :senstype="senstype"
        :sensors="sensors"
        :ctrlid="ctrlid"
        :parentid="computedAction.id"
        :page-message = "PageMessage"
        @delete="deleteAction"
        @SetPageMessage="SetPageMessage"
      />
    </transition-group>

    <div
      v-show="computedAction && computedAction.actionid === null && (showActions || !actions.length)"
      class="sensor-action__controls"
    >
      <button
        class="g-btn g-btn--rounded g-btn--blue"
        :class="{'disabled': disableCreate}"
        :disabled="disableCreate"
        @click.stop="createActionButtonClicked(createBlankAction())"
      >
        <span v-if="!disableCreate">{{ $t('messages.sensors.sdActions.createsubaction') }}</span>
        <font-awesome-icon
          v-else
          :icon="['fas', 'circle-notch']"
          :spin="true"
        />
      </button>
    </div>
  </div>
</template>

<script>
import {MicroService} from'@/assets/javascript/mixins/Mixins'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import InputElement from'@/components/elements/InputElement.vue'
import SelectElement from'@/components/elements/SelectElementReactive.vue'
import {convertToOptions} from '@/assets/javascript/utilities'

export default {
  name: 'EditSensorDecoderAction',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'input-element': InputElement,
    'select-element': SelectElement
  },
  mixins: [MicroService],
  props: {
    PageMessage: {
      type: Object,
      default: function() {
        return {}
      }
    },
    actionData: {
      type: Object,
      default: function() {
        return {}
      }
    },
    sensors: {
      type: Number,
      default: 0
    },
    senstype: {
      type: Number,
      default: 0
    },
    ctrlid: {
      type: Number,
      default: function() {
        return 0
      }
    },
    alarmid: {
      type: Number,
      default: function() {
        return 0
      }
    },
    parentid: {
      type: Number,
      default: null
    },
    actionDropdown: {
      type: Array,
      default: function() {
        return []
      }
    },
  },
  data() {
    return {
      types: [],
      actions: [],
      itemNoRange: {},
      disableCreate: false,
      showActions: false
    }
  },
  computed: {
    computedAction() {
      return this.actionData ? this.actionData.data : {}
    },
    computedAngle() {
      return !this.showActions ? ['fas', 'angle-up'] : ['fas', 'angle-down']
    }
  },
  watch: {
    PageMessage(msg) {
      if(msg &&msg.target === 'sensor-decoder-action-delete') {
        const match = this.actions.find(action => action.data.id === msg.data.id)

        if(match) this.deleteAction(match)
      }
    },
    computedAction: {
      handler(data) {
        this.getTypes(data.action)
        this.getSubActions(data.id)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    SetPageMessage(Message) {
      this.$emit('SetPageMessage', Message)
    },
    toggle() {
      if(this.actions.length !== 0) this.showActions = !this.showActions
    },
    createBlankAction() {
      return {
        ctrlid: this.ctrlid,
        seqno: this.actions.length + 1,
        alarmid: this.alarmid,
        index: null,
        action: null,
        itemtype: null,
        itemno: null,
        delay: null,
        forced: null,
        message: null,
        actionid: this.computedAction.actionid || this.computedAction.id || null
      }
    },
    createActionButtonClicked(data) {
      this.disableCreate = this.showActions= true
      this.sendMicroServiceRequest({
        method: 'PUT',
        url: `api/authenticated/function/createsdaction?senstype=${this.senstype}`,
        data
      })
      .then((response) => {
        if(response.data && response.data.createsdaction)
          this.actions.push(response.data.createsdaction[0])
      })
      .catch(console.error)
      .finally(() => this.disableCreate = false)
    },
    deleteClicked(data) {
      const text = this.actions.length
      ? 'Deleting this action will also delete its sub-actions.'
      : ''

      this.$emit('SetPageMessage', {
        target: 'ShowModal',
        modalData: {
          component: 'DialogueModal',
          data: {
            title: 'Delete Action?',
            prefix: 'sensor-decoder-action-',
            text,
            data,
            options: [
              {label: 'Delete', value: 'delete'}, {label: 'Cancel', value: 'cancel'}
            ]
          }
        }
      })
    },
    deleteAction(data) {
      const actionIndex = this.actions.indexOf(data)

      if(actionIndex === -1) return this.$emit('delete', data) // recursive: child not found, go up

      this.sendMicroServiceRequest({
        method: 'DELETE',
        url: `api/authenticated/function/deletesdaction?ctrlid=${this.ctrlid}&id=${data.id}`,
        data
      }).then((response) => {
        if(response.status !== 200) console.error(response)
        if(response.data && response.data.deletesdaction) this.actions.splice(actionIndex, 1)
      }).catch(console.error)
    },
    updateAction(data) {
      this.sendMicroServiceRequest({
        method: 'POST',
        url: `api/authenticated/function/updatesdaction?ctrlid=${this.ctrlid}`,
        data
      }).then((response) => {
        if(response.status !== 200) return console.error(response)

        if(response && response.data && response.data.updatesdaction)
          Object.assign(this.actionData, response.data.updatesdaction[0])
      }).catch(console.error)
    },
    getTypes(action) {
      this.sendMicroServiceRequest({
        method: 'GET',
        url: `api/authenticated/function/getsdactionsecondarydropdown?&enum=${action}&language_code=en-US`
      })
      .then((response) => {
        const setData = (response) => {
          this.$set(this, 'types', convertToOptions(response.data, 'enum', 'lang'))
          this.$set(this, 'itemNoRange', response.status)
        }

        response.data && response.data.getsdactionsecondarydropdown
        ? setData(response.data.getsdactionsecondarydropdown)
        : console.error('no data for getsdactionsecondarydropdown')
      }).catch(console.error)
    },
    setValue(option, name) {
      const value = option !== null && option !== undefined && option.value !== undefined ? option.value : option
      this.$set(this.computedAction, name, value)
      this.updateAction(Object.assign({}, this.createBlankAction(), this.computedAction))
    },
    getSubActions(actionID) {
      this.sendMicroServiceRequest({
        method: 'GET',
        url: `api/authenticated/function/getsdsubaction?ctrlid=${this.ctrlid}&alarmid=${this.alarmid}&actionid=${actionID}`
      })
      .then((response) => {
        response.data && response.data.getsdsubaction
        ? this.$set(this, 'actions', response.data.getsdsubaction.sort((a, b) => a.data.seqno - b.data.seqno))
        : console.error('no data for getsdsubaction')
      }).catch(console.error)
    }
  }
}
</script>
<style lang="scss">
.sensor-action {
  margin-left: 20px;
  overflow: hidden;
  border-left: 1px solid #abc;
  margin-left: 24px;
  padding-left: 10px;

  .action--transition {
    &-enter {
      opacity: 0;
    }
    &-enter-to {
      transition: all 0.4s;
      opacity: 1;
    }
    &-leave-to {
      transition: all 0.3s;
      opacity: 0;
    }
    // &-leave-active {
    // 	position: absolute;
    // }
    &-move {
      transition: transform 0.4s;
    }
  }
  
  &__controls {
    text-align: center;
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    border-left: 1px solid #abc;
    margin-left: 24px;
    margin-bottom: 16px;

    > * {
      transform: translateY(50%);
    }

    &:before, &:after {
      content: ' ';
      box-sizing: border-box;
      height: 50%;
      transform: translateY(100%);
      border-bottom: 1px solid #abc;

    }

    .g-btn {
      width: 100%;
      margin: 0;
    }
  }

  &__grid {
    display: flex;
    padding-bottom: 5px;

    > * {
      display: flex;
      justify-content: stretch;
      flex-direction: column;

      > *:not(h4) {
        height: 100%;
      }

      &.g-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        width: auto;
      }
    }

    .no-shrink {
      flex-shrink: 0;
    }

    .min-50 {
      flex: 1 0 50px;
    }

    .g-btn {
      margin: 0 !important;
      padding: 0 !important;

      > & {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .select-element-wrapper {
      margin: 0;
      select {
        padding: 5px !important;
      }
    }

    .input-element-wrapper {
      padding: 0;
      input {
        padding: 5px !important;
      }
    }
  }
}
</style>
